import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui components
import {
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Chip
} from "@mui/material";
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  PhoneAndroid as MobileIcon,
  Computer as DesktopIcon,
  WifiOff as OfflineIcon,
  CloudDone as OnlineIcon
} from "@mui/icons-material";

// Services
import pwaService from "services/pwaService";

// Argon components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";

function PWAInstallPrompt() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [appInfo, setAppInfo] = useState({});

  useEffect(() => {
    // Get initial app info
    setAppInfo(pwaService.getAppInfo());

    // Check if device is mobile
    const isMobileDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      // Check for mobile devices
      return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase()) ||
        (window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
    };

    // Listen for PWA events
    const handleInstallable = () => {
      // Check if on authentication pages
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath.includes('/sign-in') || 
                        currentPath.includes('/sign-up') || 
                        currentPath.includes('/authentication');
      
      // Only show install prompt on mobile devices and not on auth pages
      if (!pwaService.isInstalled() && isMobileDevice() && !isAuthPage) {
        setShowInstallPrompt(true);
      }
    };

    const handleUpdateAvailable = (event) => {
      setShowUpdateAlert(true);
    };

    const handleOnlineStatus = (event) => {
      setIsOnline(event.detail.online);
      if (!event.detail.online) {
        setShowOfflineAlert(true);
      }
    };

    // Add event listeners
    window.addEventListener('pwa:installable', handleInstallable);
    window.addEventListener('pwa:update-available', handleUpdateAvailable);
    window.addEventListener('pwa:online', handleOnlineStatus);
    window.addEventListener('pwa:offline', handleOnlineStatus);

    // Cleanup
    return () => {
      window.removeEventListener('pwa:installable', handleInstallable);
      window.removeEventListener('pwa:update-available', handleUpdateAvailable);
      window.removeEventListener('pwa:online', handleOnlineStatus);
      window.removeEventListener('pwa:offline', handleOnlineStatus);
    };
  }, []);

  const handleInstallClick = async () => {
    const accepted = await pwaService.showInstallPrompt();
    setShowInstallPrompt(false);
    
    if (accepted) {
      console.log('App installation accepted');
    }
  };

  const handleInstallDismiss = () => {
    setShowInstallPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleUpdateClick = () => {
    pwaService.skipWaiting();
    setShowUpdateAlert(false);
    // Reload after a short delay to allow service worker to activate
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Check if we should show install prompt
  const shouldShowInstall = showInstallPrompt && 
    !sessionStorage.getItem('pwa-install-dismissed') &&
    !pwaService.isInstalled();

  return (
    <>
      {/* Install Dialog */}
      <Dialog 
        open={shouldShowInstall} 
        onClose={handleInstallDismiss}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
            <ArgonBox display="flex" alignItems="center">
              <InstallIcon sx={{ mr: 1, color: 'primary.main' }} />
              <ArgonTypography variant="h5" fontWeight="medium">
                Install PRM Core
              </ArgonTypography>
            </ArgonBox>
            <IconButton onClick={handleInstallDismiss} size="small">
              <CloseIcon />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        
        <DialogContent>
          <ArgonBox mb={2}>
            <ArgonTypography variant="body1" color="text" mb={2}>
              Install PRM Core on your device for faster access and offline functionality.
            </ArgonTypography>
            
            <ArgonBox mb={2}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={1}>
                Benefits:
              </ArgonTypography>
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li>
                  <ArgonTypography variant="body2" color="text">
                    Quick access from your home screen
                  </ArgonTypography>
                </li>
                <li>
                  <ArgonTypography variant="body2" color="text">
                    Offline access to your property data
                  </ArgonTypography>
                </li>
                <li>
                  <ArgonTypography variant="body2" color="text">
                    Native app-like experience
                  </ArgonTypography>
                </li>
                <li>
                  <ArgonTypography variant="body2" color="text">
                    Background sync when online
                  </ArgonTypography>
                </li>
              </ul>
            </ArgonBox>

            <ArgonBox display="flex" gap={1} flexWrap="wrap">
              <Chip 
                icon={<MobileIcon />} 
                label="Mobile Optimized" 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              <Chip 
                icon={<OfflineIcon />} 
                label="Offline Support" 
                size="small" 
                color="secondary" 
                variant="outlined"
              />
              <Chip 
                icon={<DesktopIcon />} 
                label="Cross Platform" 
                size="small" 
                color="info" 
                variant="outlined"
              />
            </ArgonBox>
          </ArgonBox>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleInstallDismiss} color="inherit">
            Maybe Later
          </Button>
          <ArgonButton 
            variant="gradient" 
            color="primary" 
            onClick={handleInstallClick}
            startIcon={<InstallIcon />}
          >
            Install App
          </ArgonButton>
        </DialogActions>
      </Dialog>

      {/* Update Alert */}
      <Snackbar
        open={showUpdateAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: '80px !important' }}
      >
        <Alert
          severity="info"
          action={
            <ArgonBox display="flex" gap={1}>
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => setShowUpdateAlert(false)}
              >
                Later
              </Button>
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleUpdateClick}
                variant="outlined"
              >
                Update Now
              </Button>
            </ArgonBox>
          }
        >
          A new version of PRM Core is available!
        </Alert>
      </Snackbar>

      {/* Offline Alert */}
      <Snackbar
        open={showOfflineAlert}
        autoHideDuration={6000}
        onClose={() => setShowOfflineAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity="warning"
          icon={<OfflineIcon />}
          onClose={() => setShowOfflineAlert(false)}
        >
          You&apos;re offline. Some features may be limited.
        </Alert>
      </Snackbar>

      {/* Online Status Indicator */}
      {!isOnline && (
        <Box
          position="fixed"
          top={10}
          right={10}
          zIndex={9999}
        >
          <Chip
            icon={<OfflineIcon />}
            label="Offline"
            color="warning"
            size="small"
            sx={{
              backgroundColor: 'warning.main',
              color: 'white',
              '& .MuiChip-icon': {
                color: 'white'
              }
            }}
          />
        </Box>
      )}
    </>
  );
}

PWAInstallPrompt.propTypes = {};

export default PWAInstallPrompt;